import { jsonrepair } from "jsonrepair";
import { z } from "zod";
import type { A2uiMessage, DraftEmail } from "../../../types";
import { isValidA2UIMessage } from "../schemas/a2uiSchema";
import type { Event, ToolConfirmation } from "@google/adk";
import type { FunctionCall } from "@google/genai";

function getPrimitiveComponent(id: string, label: string, path: string, value: any, disabledPath: string) {
  if (typeof value === 'boolean') {
    return {
      id,
      component: "CheckBox",
      label,
      value: { path },
      disabled: { path: disabledPath }
    };
  }
  
  if (typeof value === 'number') {
    return {
      id,
      component: "TextField",
      label,
      variant: "number",
      value: { path },
      disabled: { path: disabledPath }
    };
  }
  
  if (typeof value === 'string') {
    // Check for standard ISO date (YYYY-MM-DD) or ISO datetime (YYYY-MM-DDThh:mm:ss...)
    const isDate = /^\d{4}-\d{2}-\d{2}$/.test(value);
    const isDateTime = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?(\.\d{1,3})?(Z|[+-]\d{2}:\d{2})?$/.test(value);
    
    if (isDate || isDateTime) {
      return {
        id,
        component: "DateTimeInput",
        label,
        enableDate: true,
        enableTime: isDateTime,
        value: { path },
        disabled: { path: disabledPath }
      };
    }
    
    if (value.length > 80 || value.includes('\n')) {
      return {
        id,
        component: "TextField",
        label,
        variant: "longText",
        value: { path },
        disabled: { path: disabledPath }
      };
    }
  }

  return {
    id,
    component: "TextField",
    label,
    value: { path },
    disabled: { path: disabledPath }
  };
}

function buildPayloadComponents(
  payload: any,
  basePath: string,
  parentId: string,
  disabledPath: string
): { components: any[]; childrenIds: string[] } {
  const components: any[] = [];
  const childrenIds: string[] = [];

  if (typeof payload === 'object' && payload !== null) {
    if (Array.isArray(payload)) {
      payload.forEach((item, index) => {
        const currentPath = `${basePath}/${index}`;
        const itemId = `${parentId}-item-${index}`;
        
        if (typeof item === 'object' && item !== null) {
          const nestedColId = `col-${itemId}`;
          const nested = buildPayloadComponents(item, currentPath, nestedColId, disabledPath);
          components.push({ id: nestedColId, component: "Column", children: nested.childrenIds });
          components.push(...nested.components);
          childrenIds.push(nestedColId);
        } else {
          const itemComp = getPrimitiveComponent(itemId, `Item ${index + 1}`, currentPath, item, disabledPath);
          components.push(itemComp);
          childrenIds.push(itemId);
        }
      });
    } else {
      Object.entries(payload).forEach(([key, value]) => {
        const currentPath = `${basePath}/${key}`;
        const safeKey = key.replace(/[^a-zA-Z0-9]/g, '_');
        const fieldId = `field-${parentId}-${safeKey}`;
        
        if (typeof value === 'object' && value !== null) {
          const labelId = `lbl-${fieldId}`;
          const colId = `col-${fieldId}`;
          const nested = buildPayloadComponents(value, currentPath, colId, disabledPath);
          
          components.push({ id: labelId, component: "Text", text: key, variant: "h5" });
          components.push({ id: colId, component: "Column", children: nested.childrenIds });
          components.push(...nested.components);
          
          childrenIds.push(labelId, colId);
        } else {
          const comp = getPrimitiveComponent(fieldId, key, currentPath, value, disabledPath);
          components.push(comp);
          childrenIds.push(fieldId);
        }
      });
    }
  }

  return { components, childrenIds };
}

export function generateToolConfirmationA2UI(event: Event, allEvents?: Event[]): A2uiMessage[] {
  const messages: A2uiMessage[] = [];

  if (!event.content?.parts || !Array.isArray(event.content.parts)) {
    return messages;
  }

  for (const part of event.content.parts) {
    if (part.functionCall && part.functionCall.name === "adk_request_confirmation") {
      const callId = part.functionCall.id;

      const args = part.functionCall.args as Record<string, any> | undefined;
      const toolConfirmation = args?.toolConfirmation as ToolConfirmation | undefined;
      const originalFunctionCall = args?.originalFunctionCall as FunctionCall | undefined;

      if (!callId || !toolConfirmation) continue;
      const surfaceId = `tool-confirmation-${callId}`;
      const title = `Confirmation Required: ${originalFunctionCall?.name?.replace(/_/g, ' ') || 'Tool Call'}`;
      const desc = toolConfirmation.hint || "The agent wants to execute a tool. Please confirm.";
      const payload = toolConfirmation.payload || {};

      let isConfirmed = toolConfirmation.confirmed === true;

      if (!isConfirmed && allEvents) {
        isConfirmed = allEvents.some(e => 
          e.content?.parts?.some((p) => 
            p.functionResponse?.id === callId && p.functionResponse?.response?.confirmed === true
          )
        );
      }

      const { components: payloadComps, childrenIds: payloadChildIds } = buildPayloadComponents(
        payload,
        "/payload",
        callId,
        "/isConfirmed"
      );

      messages.push({
        createSurface: {
          surfaceId,
          catalogId: "standard"
        }
      } as A2uiMessage);

      messages.push({
        updateDataModel: {
          surfaceId,
          value: {
            isConfirmed,
            confirmText: isConfirmed ? "✓ Confirmed" : "Confirm",
            isPrimary: !isConfirmed,
            payload
          }
        }
      } as A2uiMessage);

      messages.push({
        updateComponents: {
          surfaceId,
          components: [
            { id: "main", component: "Card", child: "col" },
            { id: "col", component: "Column", children: ["title", "desc", ...payloadChildIds, "actions"] },
            { id: "title", component: "Text", text: title, variant: "h3" },
            { id: "desc", component: "Text", text: desc, variant: "body" },
            ...payloadComps,
            { id: "actions", component: "Row", children: ["btnConfirm"], justify: "end" },
            {
              id: "btnConfirm",
              component: "Button",
              child: "lblConfirm",
              primary: { path: "/isPrimary" } as any,
              disabled: { path: "/isConfirmed" } as any,
              action: {
                event: {
                  name: "confirmFunctionCall",
                  context: {
                    functionCall: part.functionCall,
                    toolConfirmationConfirmed: true,
                    payload: { path: "/payload" }
                  }
                }
              }
            },
            { id: "lblConfirm", component: "Text", text: { path: "/confirmText" } as any }
          ]
        }
      } as A2uiMessage);
    }
  }

  return messages;
}
export class TokenStreamParser {
  private state: 'TEXT' | 'BUFFERING_JSON' = 'TEXT';
  private buffer = '';
  
  private startDelimiter = '---a2ui_JSON_START---';
  private endDelimiter = '---a2ui_JSON_END---';

  private onTextChunk: (text: string) => void;
  private onA2UIMessages: (messages: A2uiMessage[]) => void;
  private onDraftEmail?: (draft: DraftEmail) => void;
  private onBufferingChange: (isBuffering: boolean) => void;

  constructor(handlers: {
    onTextChunk: (text: string) => void;
    onA2UIMessages: (messages: A2uiMessage[]) => void;
    onDraftEmail?: (draft: DraftEmail) => void;
    onBufferingChange: (isBuffering: boolean) => void;
  }) {
    this.onTextChunk = handlers.onTextChunk;
    this.onA2UIMessages = handlers.onA2UIMessages;
    this.onDraftEmail = handlers.onDraftEmail;
    this.onBufferingChange = handlers.onBufferingChange;
  }

  public processChunk(chunk: string) {
    if (!chunk) return;
    
    this.buffer += chunk;
    this.processBuffer();
  }

  private processBuffer() {
    let advanced = true;
    while (advanced) {
      advanced = false;

      if (this.state === 'TEXT') {
        const startIndex = this.buffer.indexOf(this.startDelimiter);
        
        if (startIndex !== -1) {
          // Found start delimiter. Emit text before it.
          const textBefore = this.buffer.substring(0, startIndex);
          if (textBefore) {
            this.emitText(textBefore);
          }
          
          this.state = 'BUFFERING_JSON';
          this.onBufferingChange(true);
          this.buffer = this.buffer.substring(startIndex + this.startDelimiter.length);
          advanced = true;
        } else {
          // No start delimiter found yet. Emit safe text length.
          const safeEmitLength = Math.max(0, this.buffer.length - this.startDelimiter.length + 1);
          if (safeEmitLength > 0) {
            this.emitText(this.buffer.substring(0, safeEmitLength));
            this.buffer = this.buffer.substring(safeEmitLength);
          }
        }
      } else if (this.state === 'BUFFERING_JSON') {
        const endIndex = this.buffer.indexOf(this.endDelimiter);
        
        if (endIndex !== -1) {
          // Found end delimiter.
          const jsonStr = this.buffer.substring(0, endIndex);
          this.parseAndEmitJSON(jsonStr);
          
          this.state = 'TEXT';
          this.onBufferingChange(false);
          this.buffer = this.buffer.substring(endIndex + this.endDelimiter.length);
          advanced = true;
        }
      }
    }
  }

  public flush() {
    if (this.state === 'TEXT' && this.buffer.length > 0) {
      this.emitText(this.buffer);
      this.buffer = '';
    } else if (this.state === 'BUFFERING_JSON') {
      this.parseAndEmitJSON(this.buffer);
      this.buffer = '';
      this.state = 'TEXT';
      this.onBufferingChange(false);
    }
  }

  private emitText(text: string) {
    this.onTextChunk(text);
  }

  private parseAndEmitJSON(jsonStr: string) {
    if (!jsonStr.trim()) return;
    try {
      const repaired = jsonrepair(jsonStr.trim());
      const parsed = JSON.parse(repaired);
      
      const toEmitA2UI: A2uiMessage[] = [];
      const items = Array.isArray(parsed) ? parsed : [parsed];
      
      for (const item of items) {
         if (item && typeof item === "object") {
             if ('updateDataModel' in item && typeof item.updateDataModel === "object" && item.updateDataModel !== null) {
                const udm = item.updateDataModel as any;
                // Only auto-inject if the surface is 'inject_draft'
                if (udm.surfaceId === "inject_draft" && (udm.path === "/emailDraft" || udm.path === "emailDraft") && udm.value) {
                   if (this.onDraftEmail) {
                      this.onDraftEmail(udm.value);
                   }
                }
             }

             if (isValidA2UIMessage(item)) {
                toEmitA2UI.push(item);
             } else {
                console.warn("A2UI Validation Failed:", item);
             }
         }
      }
      
      if (toEmitA2UI.length > 0) {
         this.onA2UIMessages(toEmitA2UI);
      }
    } catch (e) {
      console.warn("Failed to parse buffered JSON:", e);
    }
  }
}
